import type { IntakeRow, RuleContext, RuleSpec, ValidationIssue } from './types.js';

/**
 * Rule registry. Each rule is registered with its bucket so the same
 * registry can serve both client-side (only CLIENT_OFFLINE_OK + CLIENT_WITH_CACHE)
 * and server-side validation paths.
 *
 * Rules are added via `registerRule(spec)`. The parity CI gate exercises
 * every registered CLIENT_OFFLINE_OK and CLIENT_WITH_CACHE rule against
 * identical fixtures on both the TS and Python sides.
 */
const REGISTRY = new Map<string, RuleSpec>();

export function registerRule(spec: RuleSpec): void {
  if (REGISTRY.has(spec.code)) {
    throw new Error(`Validation rule already registered: ${spec.code}`);
  }
  REGISTRY.set(spec.code, spec);
}

export function getRule(code: string): RuleSpec | undefined {
  return REGISTRY.get(code);
}

export function listRules(): RuleSpec[] {
  return Array.from(REGISTRY.values());
}

/**
 * Run every registered offline-capable rule against a row. Used by the
 * field PWA before queueing a submission and by the parity CI gate.
 *
 * Server-only rules are skipped — they run after sync on the backend.
 */
export function validateRowOffline(row: IntakeRow, ctx: RuleContext): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const spec of REGISTRY.values()) {
    if (spec.bucket === 'SERVER_ONLY') continue;
    const ruleConfig = ctx.rules[spec.code];
    if (ruleConfig && !ruleConfig.is_enabled) continue;
    issues.push(...spec.fn(row, ctx));
  }
  // Stable order so the parity diff isn't sensitive to map iteration order
  // (which is insertion order in JS, but Python may differ). Sort by rule
  // code, then field, then message.
  issues.sort((a, b) => {
    if (a.rule !== b.rule) return a.rule.localeCompare(b.rule);
    if ((a.field ?? '') !== (b.field ?? '')) return (a.field ?? '').localeCompare(b.field ?? '');
    return a.message.localeCompare(b.message);
  });
  return issues;
}
